import {
  Button,
  Classes,
  ControlGroup,
  FormGroup,
  H5, Icon,
  InputGroup,
  Intent, Label,
  MenuItem,
  Overlay, Position,
  Spinner, Tooltip,
  RadioGroup, Radio
} from "@blueprintjs/core";
import React, {useContext, useState, useEffect} from "react";
import {MultiSelect} from "@blueprintjs/select";
import Service from "../Service";
import AppContext from "../AppContext";

const identityListName = 'Identity'
const googleWorkspaceTypeName = 'Google Workspace'
const RegionsMultiSelect = MultiSelect.ofType();
var enableDebugging
var apiVersion
var showCreditsDistribution

function debug(debugMessage) {
  if(!enableDebugging) {
    return
  }

  console.log(debugMessage)
}

function makeID(name) {
  return name.replace(/\s/g, '-').replace(/\W/g, '').toLowerCase()
}

function _get(source, path) {
  return path.split('.').reduce((target, each) => {
    return target[each]
  }, source)
}

function FieldLabelWithInfo({label, tooltip = [
  'Select the storage region or regions that best suits your business requirements.'
]}) {
  return (
    <ControlGroup vertical={false} className="field-label-with-info">
      <Label>{label}</Label>
      {tooltip && (
        <Tooltip content={
          <div>
            {tooltip.map(each => <div>{each}</div>)}
          </div>
        } position={Position.TOP}>
          <Icon icon="info-sign" />
        </Tooltip>
      )}
    </ControlGroup>
  )
}

function CreditsDistribution({listName, types, credits, disabled = false, onChange = ()=> {}, showMode = 'remaining'}) {
  const typesList = Array.isArray(types) ? types : ['TEST']
  const creditsNum = typeof credits === 'number' ? credits : Number(credits) || 0

  const [consumptions, setConsumptions] = useState({})
  const [maxConsumptions, setMaxConsumptions] = useState(0)
  const [totalConsumed, setTotalConsumed] = useState(0)
  const [remainingConsumed, setRemainingConsumed] = useState(0)

  useEffect(() => {
    const total = Object.values(consumptions).reduce((total, each) => total + each, 0)
    setTotalConsumed(total)
    setRemainingConsumed(creditsNum - total)
  }, [consumptions, creditsNum])

  useEffect(() => {
    onChange({
      listName,
      consumptions,
      totalConsumed
    })
  }, [totalConsumed])

  useEffect(() => {
    setConsumptions(typesList.reduce((acc, t) => {
      acc[t] = 0
      return acc
    }, {}))
  }, [types])

  if (!(listName && typesList.length && creditsNum > 0)) {
    return null
  }

  return (
    <section className="credits-distribution-wrap">
      <div className="bp4-callout">
        Thank you for your purchase that includes <strong>{creditsNum}</strong> user licenses for SaaS Apps.
        <br/>
        Please tell us how you would like to utilize these user licenses.
      </div>
      <div className="apps-list">
        {typesList.map((each, key) => (
            <FormGroup
                key={key}
                helperText=""
                label={`${each}`}
                labelFor={`${makeID(each)}-licenses`}
            >
              <InputGroup id={makeID(each)}
                          disabled={disabled}
                          onChange={(e) => {
                            setConsumptions({
                              ...consumptions,
                              [each]: Number(e.target.value)
                            })
                          }}
                          value={consumptions[each] ?? ''}
                          placeholder="" />
            </FormGroup>
        ))}
      </div>
      {
        showMode === 'remaining' && remainingConsumed !== 0 ?
          <small className="danger-text">
            Remaining Licenses <strong>{remainingConsumed}</strong>.
            <br/>Utilized licenses do not equal the total purchased.
          </small> :
        showMode === 'total' && remainingConsumed !== 0 ?
          <small className="danger-text">
            Total Licenses <strong>{totalConsumed}</strong>.
            <br/>Utilized licenses do not equal the total purchased.
          </small> : null
      }

    </section>
  )
}

function isCreditsDistributionActive(saasLicenses) {
  return showCreditsDistribution && (saasLicenses?.credits ?? 0) > 0
}

function hasGoogleWorkspaceCredits(productDistribution, listName) {
  const value = productDistribution?.[listName]?.[googleWorkspaceTypeName]
  return value !== undefined && value !== null && value !== '' && Number(value) > 0
}

function M365RegionsInfo({listName, credits, showCreditsDistribution = true}) {
  const creditsNum = typeof credits === 'number' ? credits : Number(credits) || 0

  if (!(showCreditsDistribution && listName && creditsNum > 0)) {
    return null
  }

  return (
    <section className="m365-regions-info-wrap">
      <div className="bp3-callout">
        <ul className="m365-regions-info-list">
          <li>AWS Regions: Support all workloads</li>
          <li>Azure Regions: Available for M365 only, while Non-M365 apps require AWS.</li>
          <li>Provide both AWS and Azure regions if assigning M365 to Azure.</li>
        </ul>
      </div>
    </section>
  )
}

function OrderInfo({listName, productNames}) {
  
  debug(`OrderInfo - listName: ${listName}`)
  debug(`OrderInfo - productNames: ${productNames}`)

  if (!productNames || productNames.length === 0) {
    return null
  }

  return (
    <section className="order-info-wrap">
      <div className="bp4-callout">
        {
          productNames && productNames.length === 1 ? (
            <p>Thank you for your purchase of {productNames[0]}.</p>
          ) : (
            <>
              <p>Thank you for your purchase of</p>
              <ul>
                {productNames.map((each, key) => (
                  <li key={key}>{each}</li>
                ))}
              </ul>
            </>
          )
        }
      </div>    
    </section>
  )
}

function workloadIncludes(workloads, term) {
  if (!workloads) return false
  const list = Array.isArray(workloads) ? workloads : [workloads]
  return list.some(w => String(w).trim().toUpperCase().includes(term))
}

function cloudStorageIncludesAWS(value) {
  return value === 'aws' || value === 'awsAndAzure'
}

function cloudStorageIncludesAzure(value) {
  return value === 'azure' || value === 'awsAndAzure'
}

function isCloudStorageOptionEnabled(value, enabledOptions) {
  return enabledOptions.some(o => o.value === value)
}

function getCloudStorageOptions(productFamily, productWorkload, data) {
  const CLOUD_STORAGE_OPTIONS = {
    AWS: { value: 'aws', label: 'AWS', show: true, requiresAWS: true },
    AZURE: { value: 'azure', label: 'Azure', show: true, requiresAzure: true },
    AWSAZURE: { value: 'awsAndAzure', label: 'AWS + Azure', show: true, requiresAWS: true, requiresAzure: true },
  }

  const hasAWS = (data?.awsProductRegions[productFamily]?.length ?? 0) > 0
  const hasAzure = (data?.azureProductRegions[productFamily]?.length ?? 0) > 0

  if (workloadIncludes(productWorkload, 'POWER PLATFORM')) {
    CLOUD_STORAGE_OPTIONS.AWS.show = false
    CLOUD_STORAGE_OPTIONS.AWSAZURE.show = false
  } else if (workloadIncludes(productWorkload, 'MICROSOFT 365')) {
    CLOUD_STORAGE_OPTIONS.AZURE.show = false
  }

  return Object.values(CLOUD_STORAGE_OPTIONS).filter(option => {
    if (!option.show) return false
    if (option.requiresAWS && !hasAWS) return false
    if (option.requiresAzure && !hasAzure) return false
    return true
  })
}

function getRegionAvailability(invitation, productFamily) {
  return {
    hasAWS: (invitation?.awsProductRegions?.[productFamily]?.length ?? 0) > 0,
    hasAzure: (invitation?.azureProductRegions?.[productFamily]?.length ?? 0) > 0,
  }
}

function getIdentityCloudStorage(hasAWS, hasAzure) {
  if (hasAWS && hasAzure) return 'awsAndAzure'
  if (hasAWS) return 'aws'
  if (hasAzure) return 'azure'
  return null
}

function shouldShowAWSRegionPicker(productFamily, cloudStorage, hasAWS, enabledOptions, formData, invitation) {
  if (productFamily === identityListName) {
    return hasAWS
  }

  const saasLicenses = invitation?.saasLicenses?.[productFamily]
  if (isCreditsDistributionActive(saasLicenses)) {
    return hasAWS
  }

  return hasAWS &&
    cloudStorageIncludesAWS(cloudStorage) &&
    isCloudStorageOptionEnabled(cloudStorage, enabledOptions)
}

function shouldShowAzureRegionPicker(productFamily, cloudStorage, hasAzure, enabledOptions, formData, invitation) {
  if (productFamily === identityListName) {
    return hasAzure
  }

  const saasLicenses = invitation?.saasLicenses?.[productFamily]
  if (isCreditsDistributionActive(saasLicenses)) {
    return hasAzure && !hasGoogleWorkspaceCredits(formData?.productDistribution, productFamily)
  }

  return hasAzure &&
    cloudStorageIncludesAzure(cloudStorage) &&
    isCloudStorageOptionEnabled(cloudStorage, enabledOptions)
}

function getResolvedCloudStorage(productFamily, formData, invitation) {
  const { hasAWS, hasAzure } = getRegionAvailability(invitation, productFamily)
  if (!hasAWS && !hasAzure) return null

  if (productFamily === identityListName) {
    return getIdentityCloudStorage(hasAWS, hasAzure)
  }

  const saasLicenses = invitation?.saasLicenses?.[productFamily]
  if (isCreditsDistributionActive(saasLicenses)) {
    if (hasGoogleWorkspaceCredits(formData?.productDistribution, productFamily)) {
      return 'aws'
    }
    if (hasAWS && hasAzure) {
      return 'awsAndAzure'
    }
    if (hasAWS) return 'aws'
    if (hasAzure) return 'azure'
    return null
  }

  const productWorkload = saasLicenses?.types
  const enabledOptions = getCloudStorageOptions(productFamily, productWorkload, invitation)
  if (enabledOptions.length === 0) return null

  const enabledValues = enabledOptions.map(o => o.value)
  const current = formData.cloudStorage[productFamily]

  if (current && enabledValues.includes(current)) {
    return current
  }

  if (enabledOptions.length === 1) {
    return enabledOptions[0].value
  }

  if (hasAWS && !hasAzure) return 'aws'
  if (!hasAWS && hasAzure) return 'azure'

  return ''
}

function getCloudStorageRegionFields(formData, invitation) {
  const fields = []

  for (const productFamily of invitation?.products ?? []) {
    const { hasAWS, hasAzure } = getRegionAvailability(invitation, productFamily)
    if (!hasAWS && !hasAzure) continue

    const cloudStorage = getResolvedCloudStorage(productFamily, formData, invitation)
    if (!cloudStorage) continue

    const productWorkload = invitation?.saasLicenses?.[productFamily]?.types
    const enabledOptions = getCloudStorageOptions(productFamily, productWorkload, invitation)
    const saasLicenses = invitation?.saasLicenses?.[productFamily]
    if (
      productFamily !== identityListName &&
      !isCreditsDistributionActive(saasLicenses) &&
      !isCloudStorageOptionEnabled(cloudStorage, enabledOptions)
    ) continue

    if (shouldShowAWSRegionPicker(productFamily, cloudStorage, hasAWS, enabledOptions, formData, invitation)) {
      const regions = formData?.awsRegions?.[productFamily] ?? []
      if (regions.length === 0) {
        fields.push(`awsRegions[${productFamily}]:AWS ${productFamily} Storage Region:required`)
      }
    }

    if (shouldShowAzureRegionPicker(productFamily, cloudStorage, hasAzure, enabledOptions, formData, invitation)) {
      const regions = formData?.azureRegions?.[productFamily] ?? []
      if (regions.length === 0) {
        fields.push(`azureRegions[${productFamily}]:Azure ${productFamily} Storage Region:required`)
      }
    }
  }

  return fields
}

function OpenState(props) {
  debug(`OpenState - props: ${JSON.stringify(props)}`)

  const appCtx = useContext(AppContext)

  const [submitting, setSubmitting] = useState(false)
  const [canSubmit, setCanSubmit] = useState(false)

  const invitation = props?.invitation
  const regionTypes = invitation?.products
  const saasLicenses = invitation?.saasLicenses
  const publicDomains = invitation?.publicDomains || []
  enableDebugging = props?.debugMode ? props.debugMode : false
  apiVersion = props?.apiVersion ? props.apiVersion : '1.0'
  showCreditsDistribution = props?.showCreditsDistribution ? props.showCreditsDistribution : true

  debug(`OpenState - invitation: ${JSON.stringify(invitation)}`)
  debug(`OpenState - regionTypes: ${regionTypes}`)  

  const baseFormFields = [
    'firstName:First Name:required',
    'lastName:Last Name:required',    
    'adminEmail:Email Address:required,email,noPublicEmailDomains',        
    // ...regionTypes.map(t => `regions.${t}:${t}:required`),
    ...Object.keys(saasLicenses)
        .filter(t => saasLicenses[t].credits > 0)
        .map(t => `productDistribution.${t}:${t}:required,creditsAccountedFor`)
  ]  

  function getFormFields() {
    return [...baseFormFields, ...getCloudStorageRegionFields(formData, invitation)]
  }

  debug(`OpenState - baseFormFields: ${baseFormFields}`)

  const Validators = {
    required: {
      message() { return `Please enter some information in the field.` },
      validate(val) {
        if (val instanceof Array) {
          return !!val.length
        } else if (typeof val === 'object') {
          return !!Object.keys(val).length
        } else if (val && val.trim) {
          return !!val.trim().length
        } else {
          return !!val
        }
      }
    },
    chooseOne: {
      message() { return `Please choose one or more from the list.` },
      validate(val) {
        if (val instanceof Array) {
          return !!val.length
        } else if (typeof val === 'object') {
          return !!Object.keys(val).length
        } else if (val && val.trim) {
          return !!val.trim().length
        } else {
          return !!val
        }
      }
    },
    email: {
      message() { return `Please enter a valid email address.` },
      validate(val) { return /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i.test(val) },
    },
    noPublicEmailDomains: {
      message() { return `Please enter a work email address - not from public email domains.` },
      validate(val) {
        return publicDomains.filter(d => {
          return new RegExp(`\\@${d}$`).test(val)
        }).length === 0
      }
    },
    creditsAccountedFor: {
      message() { return false },
      validate(val, fieldName) {
        const data = saasLicenses[fieldName.split('.').pop()]
        if (Object.keys(val).length !== data.types.length) {
          return false
        }
        const totalConsumption = Object.values(val).reduce((total, each) => total + each, 0)
        if (totalConsumption !== data.credits) {
          return false
        }
        return true
      }
    }
  }

  const [formData, setFormData] = useState({
    productDistribution: Object.keys(saasLicenses).reduce((acc, each) => {
      acc[each] = {}
      return acc
    }, {}),
    awsRegions: regionTypes.reduce((acc, each) => {
      acc[each] = []
      return acc
    }, {}),
    azureRegions: regionTypes.reduce((acc, each) => {
      acc[each] = []
      return acc
    }, {}),
    cloudStorage: regionTypes.reduce((acc, each) => {
      acc[each] = ''
      return acc
    }, {})
  })

  const [dirtyState, setDirtyState] = useState({})
  const [validState, setValidState] = useState({})

  useEffect(() => {
    setFormData(prev => {
      const cloudStorage = { ...prev.cloudStorage }
      const awsRegions = { ...prev.awsRegions }
      const azureRegions = { ...prev.azureRegions }
      let changed = false

      for (const productFamily of invitation?.products ?? []) {
        const resolved = getResolvedCloudStorage(productFamily, prev, invitation)
        if (resolved !== null && resolved !== cloudStorage[productFamily]) {
          cloudStorage[productFamily] = resolved
          changed = true
        }

        if (
          isCreditsDistributionActive(invitation?.saasLicenses?.[productFamily]) &&
          hasGoogleWorkspaceCredits(prev.productDistribution, productFamily) &&
          (azureRegions[productFamily]?.length ?? 0) > 0
        ) {
          azureRegions[productFamily] = []
          changed = true
        }

        const effectiveCloudStorage = cloudStorage[productFamily]
        if (!effectiveCloudStorage) continue

        const { hasAWS } = getRegionAvailability(invitation, productFamily)
        const productWorkload = invitation?.saasLicenses?.[productFamily]?.types
        const enabledOptions = getCloudStorageOptions(productFamily, productWorkload, invitation)
        const showAWSRegionPicker = shouldShowAWSRegionPicker(
          productFamily,
          effectiveCloudStorage,
          hasAWS,
          enabledOptions,
          prev,
          invitation
        )

        const availableRegions = invitation?.awsProductRegions?.[productFamily] ?? []
        if (showAWSRegionPicker && availableRegions.length === 1) {
          const singleRegion = availableRegions[0]
          const currentRegions = awsRegions[productFamily] ?? []
          if (!currentRegions.find(e => e.name === singleRegion.name)) {
            awsRegions[productFamily] = [singleRegion]
            changed = true
          }
        }
      }

      return changed ? { ...prev, cloudStorage, awsRegions, azureRegions } : prev
    })
  }, [invitation, formData.cloudStorage, formData.productDistribution])

  useEffect(() => {
    setValidState({})
    validateForm()    
  }, [formData])

  function validateForm() {
    let isValid, messaging = {}
    const formFields = getFormFields()
    try {
      const r = formFields.map(fld => {
        const [fieldName, fieldLabel, validators] = fld.split(':')
        if (validators.length) {
          const vals = validators.split(',')
          return vals.reduce((acc, vName) => {
            const validator = Validators[vName]
            const result = validator.validate(_get(formData, fieldName), fieldName, formData)
            if (result) {
              acc++
              if (!messaging[fieldName]) {
                messaging = {
                  ...messaging,
                  [fieldName]: null
                }
              }
            } else if (!messaging[fieldName] && validator.message()) {
              messaging = {
                ...messaging,
                [fieldName]: validator.message()
              }
            }
            return acc
          }, 0) === vals.length
        }
        return true
      })
      isValid = r.filter(valid => !!valid).length === formFields.length
    } catch(e) {
      isValid = false
    }

    debug(`validateForm - messaging: ${JSON.stringify(messaging)}`)

    setValidState(messaging)
    setCanSubmit(isValid)

    return isValid
  }

  function handleCreditsDistributionChange({listName, consumptions, totalConsumed}) {
    if (listName && consumptions) {
      setFormData(prev => {
        const productDistribution = {
          ...prev.productDistribution,
          [listName]: consumptions
        }
        const cloudStorage = { ...prev.cloudStorage }
        let azureRegions = prev.azureRegions

        if (hasGoogleWorkspaceCredits(productDistribution, listName)) {
          cloudStorage[listName] = 'aws'
          if ((prev.azureRegions[listName]?.length ?? 0) > 0) {
            azureRegions = {
              ...prev.azureRegions,
              [listName]: []
            }
          }
        } else {
          const { hasAWS, hasAzure } = getRegionAvailability(invitation, listName)
          if (isCreditsDistributionActive(invitation?.saasLicenses?.[listName]) && hasAWS && hasAzure) {
            cloudStorage[listName] = 'awsAndAzure'
          }
        }

        return {
          ...prev,
          productDistribution,
          cloudStorage,
          azureRegions
        }
      })
    }
  }

  function CloudStorage({formData, listName, options}) {
    debug(`handleCloudStorageChange - listName: ${JSON.stringify(listName)}`)

    const handleCloudStorageChange = (e) => {
      const cloudStorage = e.currentTarget.value

      debug(`handleCloudStorageChange - cloudStorage for ${listName}: ${cloudStorage}`)

      if (listName && cloudStorage) {
        debug(`handleCloudStorageChange - INSIDE listName: ${JSON.stringify(listName)}`)

        setFormData(prev => {
          const updates = {
            ...prev,
            cloudStorage: {
              ...prev.cloudStorage,
              [listName]: cloudStorage
            }
          }
          if (cloudStorage === 'aws' && prev.azureRegions[listName]?.length > 0) {
            updates.azureRegions = {
              ...prev.azureRegions,
              [listName]: []
            }
          } else if (cloudStorage === 'azure' && prev.awsRegions[listName]?.length > 0) {
            updates.awsRegions = {
              ...prev.awsRegions,
              [listName]: []
            }
          }
          return updates
        })

        setDirtyState(prev => ({...prev, [`cloudStorage[${listName}]`]: true}))
      }
    }
    
    return (     
      <>
      <FormGroup            
            helperText=""
            label={<FieldLabelWithInfo label="Cloud Storage"
                                        tooltip={[
                                          'Azure storage currently supports limited workloads — contact your account team for details.'
                                        ]} />}
            labelFor={`cloud-storage-${listName}`}
          >            
        <RadioGroup 
          inline="true" 
          onChange={handleCloudStorageChange}         
          selectedValue={formData?.cloudStorage[listName]}>
          {options.map(({ value, label }) => (
            <Radio key={value} name={value} label={label} value={value} />
          ))}
        </RadioGroup>
      </FormGroup>
      {       
        !formData?.cloudStorage[listName] ?
          <small className="danger-text">
            Please select a <strong>Cloud Storage</strong> option<br /><br />
          </small> : null
      }
      </> 
    )
  }

  function renderProductDetails(data, listName, key) {
    debug(`renderProductDetails - data: ${JSON.stringify(data)}`)

    const saasLicenses = data?.saasLicenses[listName]
    const hasAWSRegions = data?.awsProductRegions[listName] && data?.awsProductRegions[listName].length > 0 ? true : false
    const hasAzureRegions = data?.azureProductRegions[listName] && data?.azureProductRegions[listName].length > 0 ? true : false
    const productWorkload = data?.saasLicenses?.[listName]?.types ?? null
    const enabledOptions = getCloudStorageOptions(listName, productWorkload, data)
    const showCredits = showCreditsDistribution && saasLicenses && saasLicenses.credits > 0
    const showCloudProviderSelection = !showCredits && listName !== identityListName && enabledOptions.length > 1

    const selectedCloudStorage = getResolvedCloudStorage(listName, formData, data) || ''

    const showAWSRegionPicker = shouldShowAWSRegionPicker(
      listName,
      selectedCloudStorage,
      hasAWSRegions,
      enabledOptions,
      formData,
      data
    )

    const showAzureRegionPicker = shouldShowAzureRegionPicker(
      listName,
      selectedCloudStorage,
      hasAzureRegions,
      enabledOptions,
      formData,
      data
    )

    const azureRegionLabel = showCredits && showAzureRegionPicker
      ? 'Azure M365 Storage Region'
      : `Azure ${listName} Storage Region`

    debug(`renderProductDetails - showCreditsDistribution: ${showCreditsDistribution}`)
    debug(`renderProductDetails - apiVersion: ${apiVersion}`)
    debug(`renderProductDetails - saasLicenses: ${JSON.stringify(saasLicenses)}`)
    
    

    return (
      <>
        {showCreditsDistribution && saasLicenses && saasLicenses.credits > 0 && (
          <CreditsDistribution
              listName={listName}
              onChange={handleCreditsDistributionChange}
              {...saasLicenses}
          />
        )}

        {!showCreditsDistribution && apiVersion === '2.0' && (!saasLicenses || (saasLicenses && saasLicenses.credits === 0)) && (
            <OrderInfo  
                listName={listName}
                productNames={data?.productNames}
            />
        )}
       
        {showCloudProviderSelection && (
        <CloudStorage
          id={'cloudStorage-' + key}
          formData={formData}
          listName={listName}
          options={enabledOptions}
        />
        )}

        <M365RegionsInfo
          listName={listName}
          credits={saasLicenses?.credits}
          showCreditsDistribution={showCreditsDistribution}
        />
        
        {showAWSRegionPicker && (
          <FormGroup            
            key={'aws-' + key}
            helperText=""
            label={<FieldLabelWithInfo label={`AWS ${listName} Storage Region`} />}
            labelFor={`aws-${listName}-region`}
          >
          <RegionsMultiSelect
              id={`awsRegions[${listName}]`}
              fill={true} minimal={true}
              items={data.awsProductRegions[listName]}
              itemRenderer={(item, options) => renderMultiSelectMenuItem(item, options, listName, 'aws')}
              noResults={<MenuItem disabled={true} text="No regions found" />}
              selectedItems={formData.awsRegions[listName]}
              onItemSelect={(item) => handleAddMultiSelectItem(item, listName, 'aws')}
              popoverProps={{minimal: true}}
              tagRenderer={(tag) => renderMultiSelectTag(tag)}
              tagInputProps={{
                disabled: submitting,
                inputProps: {
                  readOnly: true,
                  onBlur: () => {                    
                    setDirtyState({...dirtyState, [`awsRegions[${listName}]`]: true})
                  },
                },
                onRemove: (item, index) => handleRemoveMultiSelectItem(index, listName, 'aws'),
                rightElement: formData.awsRegions[listName].length > 0 ?
                    <Button icon="cross" minimal={true} onClick={() => clearMultiSelectList(listName, 'aws')} /> :
                    undefined,
                tagProps: {minimal: true,},
              }}
              placeholder=""
          />
          {Messaging(`awsRegions[${listName}]`)}
        </FormGroup>
        )}
        {showAzureRegionPicker && (
          <FormGroup            
            key={'azure-' + key}
            helperText=""
            label={<FieldLabelWithInfo label={azureRegionLabel} />}
            labelFor={`azure-${listName}-region`}
        >
          <RegionsMultiSelect
              id={`azureRegions[${listName}]`}
              fill={true} minimal={true}
              items={data.azureProductRegions[listName]}
              itemRenderer={(item, options) => renderMultiSelectMenuItem(item, options, listName, 'azure')}
              noResults={<MenuItem disabled={true} text="No regions found" />}
              selectedItems={formData.azureRegions[listName]}
              onItemSelect={(item) => handleAddMultiSelectItem(item, listName, 'azure')}
              popoverProps={{minimal: true}}
              tagRenderer={(tag) => renderMultiSelectTag(tag)}
              tagInputProps={{
                disabled: submitting,
                inputProps: {
                  readOnly: true,
                  onBlur: () => {                    
                    setDirtyState({...dirtyState, [`azureRegions[${listName}]`]: true})
                  },
                },
                onRemove: (item, index) => handleRemoveMultiSelectItem(index, listName, 'azure'),
                rightElement: formData.azureRegions[listName].length > 0 ?
                    <Button icon="cross" minimal={true} onClick={() => clearMultiSelectList(listName, 'azure')} /> :
                    undefined,
                tagProps: {minimal: true,},
              }}
              placeholder=""
          />
          {Messaging(`azureRegions[${listName}]`)}
        </FormGroup>
        )}        
      </>
    )
  }

  function renderMultiSelectMenuItem(item, {handleClick}, listName, cloudProvider) {
    // debug(`renderMultiSelectMenuItem - item: ${JSON.stringify(item)}`)
    // debug(`renderMultiSelectMenuItem - handleClick: ${handleClick}`)

    let isSelected
    
    if(cloudProvider === 'aws') {
      isSelected = formData.awsRegions[listName].find(e => e.name === item.name)
    } else if(cloudProvider === 'azure') {
      isSelected = formData.azureRegions[listName].find(e => e.name === item.name)
    }

    return <MenuItem key={item.name}
                     icon={isSelected ? "tick" : "blank"}
                     text={item.displayName}
                     shouldDismissPopover={false}
                     onClick={handleClick} />
  }

  function renderMultiSelectTag(item) {
    return `${item.displayName}`
  }

  function handleAddMultiSelectItem(item, listName, cloudProvider) {
    if (cloudProvider === 'aws') {
      setFormData(prev => {
        if (prev.awsRegions[listName]?.find(e => e.name === item.name)) {
          return prev
        }
        return {
          ...prev,
          awsRegions: {
            ...prev.awsRegions,
            [listName]: [
              ...(prev.awsRegions[listName] ?? []),
              item
            ]
          }
        }
      })
    } else if (cloudProvider === 'azure') {
      setFormData(prev => {
        if (prev.azureRegions[listName]?.find(e => e.name === item.name)) {
          return prev
        }
        return {
          ...prev,
          azureRegions: {
            ...prev.azureRegions,
            [listName]: [
              ...(prev.azureRegions[listName] ?? []),
              item
            ]
          }
        }
      })
    }
  }

  function handleRemoveMultiSelectItem(index, listName, cloudProvider) {
    if (cloudProvider === 'aws') {
      setFormData(prev => ({
        ...prev,
        awsRegions: {
          ...prev.awsRegions,
          [listName]: [
            ...prev.awsRegions[listName].slice(0, index),
            ...prev.awsRegions[listName].slice(index + 1),
          ]
        }
      }))
    } else if (cloudProvider === 'azure') {
      setFormData(prev => ({
        ...prev,
        azureRegions: {
          ...prev.azureRegions,
          [listName]: [
            ...prev.azureRegions[listName].slice(0, index),
            ...prev.azureRegions[listName].slice(index + 1),
          ]
        }
      }))
    }
  }

  function clearMultiSelectList(listName, cloudProvider) {
    debug(`clearMultiSelectList - listName: ${JSON.stringify(listName)}`)
    debug(`clearMultiSelectList - cloudProvider: ${cloudProvider}`)

    if (cloudProvider === 'aws') {
      setFormData(prev => ({
        ...prev,
        awsRegions: {
          ...prev.awsRegions,
          [listName]: []
        }
      }))
    } else if (cloudProvider === 'azure') {
      setFormData(prev => ({
        ...prev,
        azureRegions: {
          ...prev.azureRegions,
          [listName]: []
        }
      }))
    }
  }

  async function handleFormSubmit() {
    const formFields = getFormFields()
    debug(`handleFormSubmit - formFields: ${JSON.stringify(formFields)}`)

    if (!canSubmit) {
      const dirty = {}
      formFields.forEach(fld => {
        const [fieldName, validators] = fld.split(':')
        dirty[fieldName] = true
      })
      setDirtyState(dirty)
      return
    }

    const productDetails = regionTypes.reduce((acc, product) => {
      const ret = {
        product,
        awsRegions: formData.awsRegions[product].map(region => ({name: region.name})),
        azureRegions: formData.azureRegions[product].map(region => ({name: region.name}))
      }

      if (formData.productDistribution[product]) {
        ret.productDistribution = Object.keys(formData.productDistribution[product]).map(each => ({
          name: each,
          quantity: formData.productDistribution[product][each]
        }))
      }
      acc.push(ret)
      return acc
    }, [])

    const {
      firstName, lastName, adminEmail,
      cloudStorage
    } = formData


    const toSubmit = {
      orderNumber: invitation.orderNumber,
      orderBusinessUnitId: invitation.orderBusinessUnitId,
      responseState: 'PROVIDED',
      responseDetails: {
        orderNumber: invitation.orderNumber,
        orderBusinessUnitId: invitation.orderBusinessUnitId,
        firstName, lastName, adminEmail,
        cloudStorage,
        productDetails
      }
    }

    setSubmitting(true)
    const result = await Service.submitOrderInfo(invitation.invitationId, toSubmit)
    setSubmitting(false)

    if (result?.isSuccess) {
      appCtx.setAppState({
        type: 'CONFIRM'
      })
    } else {
      appCtx.setAppState({
        type: 'ERROR',
        invitation: result
      })
    }
  }

  async function handleNotMe() {
    setSubmitting(true)
    const result = await Service.omitInvitation(invitation)
    setSubmitting(false)
    if (result?.isSuccess) {
      appCtx.setAppState({
        type: 'OMITTED',
        invitation: {}
      })
    } else {
      appCtx.setAppState({
        type: 'ERROR',
        invitation: result
      })
    }
  }

  function Messaging(fieldName) {
    return dirtyState[fieldName] && validState[fieldName] && (<small className="danger-text">{validState[fieldName]}</small>)
  }

  return (
      <section className="form-wrap">
        <h1>Let's get started with some basic information and your preferences</h1>
        <form className="bordered">
          <h4>Please provide the following information below</h4>
          <FormGroup
              helperText=""
              label="First Name"
              labelFor="first-name"
          >
            <InputGroup id="first-name" value={formData.firstName}
                        disabled={submitting}
                        onChange={(e) => {setFormData({...formData, firstName: e.target.value })}}
                        onBlur={() => setDirtyState({...dirtyState, firstName: true})}
                        placeholder="" />
            {Messaging('firstName')}
          </FormGroup>
          <FormGroup
              helperText=""
              label="Last Name"
              labelFor="last-name"
          >
            <InputGroup id="last-name" value={formData.lastName}
                        disabled={submitting}
                        onChange={(e) => {setFormData({...formData, lastName: e.target.value })}}
                        onBlur={() => setDirtyState({...dirtyState, lastName: true})}
                        placeholder="" />
            {Messaging('lastName')}
          </FormGroup>
          <FormGroup
              helperText=""
              label={<FieldLabelWithInfo label="Email Address"
                                         tooltip={[
                                          'Enter a valid corporate email address.',
                                          'A public domain email address like gmail.com is not accepted.'
                                        ]} />}
              labelFor="email-address"
          >
            <InputGroup id="email-address" value={formData.adminEmail}
                        disabled={submitting}
                        inputProps={{type: 'email'}}
                        onChange={(e) => {setFormData({...formData, adminEmail: e.target.value })}}
                        onBlur={() => setDirtyState({...dirtyState, adminEmail: true})}
                        placeholder="" />
            {Messaging('adminEmail')}
          </FormGroup>
          {
            regionTypes.map((type, i) =>
              renderProductDetails(props?.invitation, type, i)
            )
          }

          <small className="privacy-message disabled-text">
            <p>
              The requested data is necessary for the performance of the service you’ve ordered to
              <a href="https://i.dell.com/sites/doccontent/legal/terms-conditions/en/Documents/Dell-Technologies-List-for-Privacy-Statement-May-2018.pdf">Dell Technologies</a>.
              The data you’ve provided to us will be processed by Dell Technologies in accordance with our
              <a href="https://www.dell.com/learn/us/en/uscorp1/policies-privacy-country-specific-privacy-policy">Privacy Statement</a>.
            </p>
            <p>
              By clicking “Submit”, you have read our
              <a href="https://www.dell.com/learn/us/en/uscorp1/policies-privacy-country-specific-privacy-policy">Privacy Statement</a>,
              understood your rights and how
              <a href="https://i.dell.com/sites/doccontent/legal/terms-conditions/en/Documents/Dell-Technologies-List-for-Privacy-Statement-May-2018.pdf">Dell Technologies</a> uses and protects your data.
            </p>
          </small>

          <Button fill={true}
                  disabled={!canSubmit || submitting}
                  onClick={handleFormSubmit}
                  intent={Intent.PRIMARY}>
            Submit
          </Button>
          <div className="hr-with-text">
            <hr/>
            <label>OR</label>
          </div>

          <Button fill={true}
                  disabled={submitting}
                  intent={Intent.PRIMARY} outlined={true}
                  onClick={handleNotMe}>I do not have this information</Button>

          <Overlay className={Classes.OVERLAY_SCROLL_CONTAINER}
                   isOpen={submitting} usePortal={false}
                   canEscapeKeyClose={false} canOutsideClickClose={false}>
            <Spinner size={50} />
            <H5>Submitting...</H5>
          </Overlay>
        </form>

        <h6 className="disabled-text">If you have any questions or concerns, please reach out to <a href="https://support.dell.com">support.dell.com</a></h6>
      </section>
  )
}


export default OpenState
